import { Hl7Context } from '@medplum/core';
import { getSettings } from '../../queries.js';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { ENV } from '../../env.js';
import { AMMS_MFN_CHANGE_TYPES, AMMS_MSH_DEFAULTS, AmmsMessageValidationError, buildAmmsMfeSegment, buildAmmsMfiSegment, buildAmmsZdrSegment, createAmmsMessage, deriveAmmsPrescriptionId, MESSAGE_TYPES, } from '../../hl7/message.js';
import { hasPriorAmmsLog, logAmmsOutboundMessage, nextAmmsControlSequence } from '../utils/amms.js';
import { zql } from '../../schema.js';
import { inUseGfrQuery } from '../../util/patient-gfr.js';
import { assertIsAuthenticated, EventLogs, getDosingWeight, getTherapySequenceInitialStatus, getTotalAmountOfSubstanceDecimal, } from '../../utils.js';
import { inputSequenceRequiresAdditionalApproval, nextLocalPrescriptionCode, } from '../patient.mutators.js';
import { addEventLog, EventLogResources } from '../util.js';
import { itemsDetail } from '../../util/event-log-items.js';
import { medicineLogItems } from './medicine-log-items.js';
import { logDoseLimitConfirmations } from './dose-limit-log.js';
import { createTherapySequence } from '../utils/therapy-sequence.js';
import { getDoseBasis } from './dose-basis.js';
import { computeTherapyDateFromCycleDay } from '../../util/protocol-cycles.js';
import { assertTotalAmountsNotZero } from './medicine-total-amount.js';
import { loadSubstanceRoundingModes } from './substance-rounding-modes.js';
export async function loadTherapyParameterContext(tx, patientId, substanceIds) {
    const characteristics = await tx.run(zql.patientCharacteristics
        .where('patientId', '=', patientId)
        .related('patient')
        .orderBy('createdAt', 'desc')
        .limit(1)
        .one());
    const patient = await tx.run(zql.patients.where('id', patientId).one());
    const gfrLab = await tx.run(inUseGfrQuery(patientId));
    const roundingModes = await loadSubstanceRoundingModes(tx, substanceIds);
    return { characteristics, patient, gfrLab, roundingModes };
}
export async function nextPrescriptionInternalId(tx, therapyDate) {
    const prescriptionDate = new Date(therapyDate);
    const internalIdStructure = await nextLocalPrescriptionCode(tx, `PR-${prescriptionDate.getFullYear()}${String(prescriptionDate.getMonth() + 1).padStart(2, '0')}${String(prescriptionDate.getDate()).padStart(2, '0')}`);
    // The numeric part of the internal id, e.g. 1235 for "PR-20260716-1235". It restarts every day,
    // so the AMMS prescription id is derived from the whole internalId (`deriveAmmsPrescriptionId`).
    const prescriptionNumber = internalIdStructure.maxNum + 1;
    return { internalId: `${internalIdStructure.prefixDash}${prescriptionNumber}`, prescriptionNumber };
}
/**
 * Resolves the AMMS ZDR-1/ZDR-4/ZDR-5 field values for a therapy sequence medicine, via the
 * drug catalog. ZDR-1 mirrors the legacy COMMERCIAL_NAME concatenation: drug name, then
 * ` w <carrier solution name>` if a carrier solution is set (legacy DRUG_SOLUTE_SEPARATOR
 * = ' w '), then ` % <volume> ml` if a volume is set (legacy SOLUTE_VOLUME_SEPARATOR = ' % ').
 */
async function resolveAmmsZdrFields(tx, medicine, sequence) {
    const drugCatalogItem = medicine.activeSubstanceId
        ? await tx.run(zql.drugCatalogItems
            .where('id', '=', medicine.activeSubstanceId)
            .related('activeSubstanceDetails', d => d.related('doseUnit'))
            .one())
        : undefined;
    const prescribedName = medicine.prescribedNameId
        ? await tx.run(zql.prescribedNames.where('id', '=', medicine.prescribedNameId).one())
        : undefined;
    const applicationForm = sequence.applicationFormId
        ? await tx.run(zql.applicationForms.where('id', '=', sequence.applicationFormId).one())
        : undefined;
    const carrierSolution = sequence.carrierSolutionId
        ? await tx.run(zql.drugCatalogItems.where('id', '=', sequence.carrierSolutionId).one())
        : undefined;
    const drugName = prescribedName?.name ?? drugCatalogItem?.name ?? '';
    const carrierSuffix = carrierSolution?.name ? ` w ${carrierSolution.name}` : '';
    const volumeSuffix = sequence.volume ? ` % ${sequence.volume} ml` : '';
    return {
        drugName,
        commercialName: `${drugName}${carrierSuffix}${volumeSuffix}`,
        doseAmount: String(medicine.totalAmount ?? ''),
        doseUnit: drugCatalogItem?.activeSubstanceDetails?.doseUnit?.displayName ?? 'mg',
        medicineForm: applicationForm?.name ?? '',
    };
}
/**
 * Builds and logs the outbound AMMS MFN^M01 message for a prescription (therapy sequence),
 * mirroring the legacy ASSECO_MSH/MFI/MFE/ZDR_RECORD procedures: one MFE for the prescription and
 * one ZDR per medicine. `change` selects MFE-1: 'add' → MAD, 'update' → MUP, 'delete' → MDL.
 * Server-only: HL7EventLog rows are written directly, not synced from the client.
 */
export async function logAmmsMfnMessage(tx, params) {
    if (!ENV.AMMS_SYSTEM || tx.location !== 'server')
        return;
    const { sequence, medicines, change, now } = params;
    // Instruction-note sequences carry no medicine, so there's no prescription to announce.
    if (medicines.length === 0)
        return;
    const zdrFields = await Promise.all(medicines.map(medicine => resolveAmmsZdrFields(tx, medicine, sequence)));
    const prescriptionId = deriveAmmsPrescriptionId(sequence.internalId);
    await logAmmsOutboundMessage(tx, {
        now,
        resourceRef: [
            { resource: EventLogResources.patientTherapySequence, id: sequence.id, name: sequence.internalId },
            ...medicines.map((medicine, index) => ({
                resource: EventLogResources.patientTherapySequenceMedicine,
                id: medicine.id,
                name: `${zdrFields[index]?.drugName ?? ''} ${zdrFields[index]?.doseAmount ?? ''}${zdrFields[index]?.doseUnit ?? ''}`.trim(),
            })),
        ],
        build: async () => {
            const incomplete = zdrFields.find(fields => !fields.commercialName || !fields.doseAmount);
            if (incomplete) {
                throw new AmmsMessageValidationError(`Cannot build AMMS MFN for ${sequence.internalId}: a medicine has no name or dose.`);
            }
            const context = new Hl7Context();
            const zdrSegments = zdrFields.map(fields => buildAmmsZdrSegment({
                context,
                commercialName: fields.commercialName,
                doseAmount: fields.doseAmount,
                doseUnit: fields.doseUnit,
                measurementUnit: fields.doseUnit,
                medicineForm: fields.medicineForm,
            }));
            return createAmmsMessage({
                ...AMMS_MSH_DEFAULTS,
                messageType: MESSAGE_TYPES.PRESCRIPTION_CREATE_MESSAGE_TYPE,
                sequence: await nextAmmsControlSequence(tx),
                sentAt: new Date(now),
                segments: [
                    buildAmmsMfiSegment(context),
                    buildAmmsMfeSegment({ prescriptionId, changeType: AMMS_MFN_CHANGE_TYPES[change], context }),
                    ...zdrSegments,
                ],
            });
        },
    });
}
/**
 * Re-announces an already-announced prescription to AMMS (MFN MUP on change, MDL on deletion),
 * reading its current medicines. Skipped when no MFN was ever logged for it — AMMS doesn't know
 * the prescription (e.g. created before the AMMS integration was enabled).
 */
export async function logAmmsMfnChangeForSequence(tx, params) {
    if (!ENV.AMMS_SYSTEM || tx.location !== 'server')
        return;
    const { sequenceId, change, now } = params;
    const sequence = await tx.run(zql.patientTherapySequence.where('id', '=', sequenceId).related('medicine').one());
    if (!sequence?.internalId)
        return;
    // MFNs logged before the per-prescription layout only reference the medicine rows.
    if (!(await hasPriorAmmsLog(tx, [sequence.id, ...sequence.medicine.map(medicine => medicine.id)])))
        return;
    await logAmmsMfnMessage(tx, {
        sequence: {
            id: sequence.id,
            internalId: sequence.internalId,
            applicationFormId: sequence.applicationFormId,
            carrierSolutionId: sequence.carrierSolutionId,
            volume: sequence.volume,
        },
        medicines: sequence.medicine.filter(medicine => !medicine.deletedAt),
        change,
        now,
    });
}
function buildTherapyParameterMedicineRows(tx, params) {
    const { input, therapyDate, context } = params;
    const rows = input.medicine.map(med => {
        const totalAmount = getTotalAmountOfSubstanceDecimal({
            calculationMethod: med.calculationMethod,
            dose: med.regularDose,
            doseProportion: med.doseProportion || 100,
            roundingMode: context.roundingModes.get(med.activeSubstanceId),
            dosePrecision: med.precisionDecimals,
            height: context.characteristics?.height ?? null,
            weight: getDosingWeight(context.characteristics),
            gfr: context.gfrLab?.gfr ?? null,
            bsa: context.characteristics?.bsa ?? null,
        });
        return {
            patientId: input.patientId,
            therapyDate,
            activeSubstanceId: med.activeSubstanceId,
            prescribedNameId: med.prescribedNameId,
            autIdem: input.autIdem ?? false,
            regularDose: med.regularDose,
            precisionDecimals: med.precisionDecimals,
            doseProportion: med.doseProportion ?? undefined,
            totalAmount,
            calculationMethod: med.calculationMethod,
        };
    });
    assertTotalAmountsNotZero(tx, rows.map(row => row.totalAmount));
    return rows;
}
export function logTherapyParameterMedicines(tx, params) {
    const { medicines, context, internalId, event, now, userId } = params;
    const confirmations = logDoseLimitConfirmations(tx, {
        confirmed: params.doseLimitConfirmed,
        medicines,
        therapyInternalId: internalId,
        userId,
        now,
    });
    return [
        confirmations,
        ...medicines.map(async (medicine) => addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequenceMedicine,
            resourceId: medicine.id,
            action: 'create',
            domain: 'prescription',
            event,
            patches: createPatch({}, medicine),
            performedBy: userId,
            performedAt: now,
            details: {
                patientId: context.patient?.id ?? '',
                patientName: context.patient?.fullName ?? '',
                prescribedNameId: medicine.prescribedNameId ?? '',
                activeSubstanceId: medicine.activeSubstanceId ?? '',
                therapyInternalId: internalId,
                ...itemsDetail(await medicineLogItems(tx, undefined, medicine)),
            },
        })),
    ];
}
export async function addFixedDateTherapyParameter(tx, input, ctx) {
    if (!input.fixedDate) {
        throw new Error('Either patientProtocolId or fixedDate must be provided');
    }
    assertIsAuthenticated(ctx);
    const context = await loadTherapyParameterContext(tx, input.patientId, input.medicine.map(med => med.activeSubstanceId));
    const requiresAdditionalApproval = await inputSequenceRequiresAdditionalApproval(tx, input.medicine);
    const therapyDate = input.fixedDate;
    const { internalId } = await nextPrescriptionInternalId(tx, therapyDate);
    const { sequence, medicines, now } = await createTherapySequence(tx, {
        data: {
            patientId: input.patientId,
            internalId,
            therapyDate,
            status: getTherapySequenceInitialStatus(requiresAdditionalApproval),
            isNote: false,
            note: input.note,
            therapyNote: input.therapyNote,
            pharmacyNote: input.pharmacyNote,
            time: input.time,
            carrierSolutionId: input.carrierSolutionId,
            applicationFormId: input.applicationFormId,
            duration: input.duration,
            volume: input.volume,
            volumeMode: input.volumeMode,
            responsibleDoctorId: input.responsibleDoctorId,
            ...getDoseBasis(context.characteristics, context.gfrLab),
        },
        medicines: buildTherapyParameterMedicineRows(tx, { input, therapyDate, context }),
        log: {
            event: EventLogs.PATIENT_THERAPY_SEQUENCE_ADDED,
            details: {
                id: internalId,
                patientName: context.patient?.fullName,
                patientId: input.patientId,
                therapyDate: therapyDate?.toString(),
                time: input.time,
            },
        },
    }, ctx);
    if (tx.location === 'server') {
        await Promise.all([
            ...logTherapyParameterMedicines(tx, {
                medicines,
                context,
                internalId,
                event: EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_ADDED,
                now,
                userId: ctx.user.id,
                doseLimitConfirmed: input.doseLimitConfirmed,
            }),
            logAmmsMfnMessage(tx, {
                sequence: {
                    id: sequence.id,
                    internalId,
                    applicationFormId: input.applicationFormId,
                    carrierSolutionId: input.carrierSolutionId,
                    volume: input.volume,
                },
                medicines,
                change: 'add',
                now,
            }),
        ]);
    }
}
export async function loadPatientProtocolForTherapy(tx, patientProtocolId, patientId) {
    const patientProtocol = await tx.run(zql.patientProtocols
        .related('protocol')
        .where('id', '=', patientProtocolId)
        .where('patientId', '=', patientId)
        .one());
    const protocol = patientProtocol?.protocol;
    if (!patientProtocol || !protocol) {
        throw new Error(`Patient Protocol not found: ${patientProtocolId}`);
    }
    return { ...patientProtocol, protocol };
}
async function addProtocolTherapyCycle(tx, input, params) {
    const { ctx, context, patientProtocol, cycleIndex, daysRepetition, isZeroDayProtocol } = params;
    assertIsAuthenticated(ctx);
    // a cycle may have been moved (Edit protocol), so the date comes from the cycle's own recorded start (CY-378)
    const therapyDate = computeTherapyDateFromCycleDay(patientProtocol, cycleIndex + 1, input.day ?? 0, daysRepetition, isZeroDayProtocol);
    if (therapyDate < patientProtocol.startDate) {
        throw new Error('Date cannot be before the protocol start date');
    }
    // Past cycles are skipped (recurring prescriptions only fill the remaining ones); today still counts.
    const startOfTodayUtc = new Date().setUTCHours(0, 0, 0, 0);
    if (therapyDate < startOfTodayUtc)
        return false;
    const requiresAdditionalApproval = await inputSequenceRequiresAdditionalApproval(tx, input.medicine);
    const { internalId } = await nextPrescriptionInternalId(tx, therapyDate);
    const { sequence, medicines, now } = await createTherapySequence(tx, {
        data: {
            patientId: input.patientId,
            protocolId: patientProtocol.protocolId,
            patientProtocolId: patientProtocol.id,
            internalId,
            therapyDate,
            day: input.day,
            cycle: cycleIndex + 1,
            status: getTherapySequenceInitialStatus(requiresAdditionalApproval),
            isNote: false,
            note: input.note,
            therapyNote: input.therapyNote,
            pharmacyNote: input.pharmacyNote,
            time: input.time,
            carrierSolutionId: input.carrierSolutionId,
            applicationFormId: input.applicationFormId,
            duration: input.duration,
            volume: input.volume,
            volumeMode: input.volumeMode,
            responsibleDoctorId: input.responsibleDoctorId,
            ...getDoseBasis(context.characteristics, context.gfrLab),
        },
        medicines: buildTherapyParameterMedicineRows(tx, { input, therapyDate, context }),
        log: {
            event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_ADDED,
            details: {
                id: internalId,
                patientName: context.patient?.fullName ?? '',
                patientId: input.patientId,
                protocolName: patientProtocol.protocol?.name ?? '',
                cycle: String(cycleIndex + 1),
                day: input.day?.toString() ?? '',
            },
        },
    }, ctx);
    if (tx.location === 'server') {
        await Promise.all([
            ...logTherapyParameterMedicines(tx, {
                medicines,
                context,
                internalId,
                event: EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_PROTOCOL_ADDED,
                now,
                userId: ctx.user.id,
                doseLimitConfirmed: input.doseLimitConfirmed,
            }),
            logAmmsMfnMessage(tx, {
                sequence: {
                    id: sequence.id,
                    internalId,
                    applicationFormId: input.applicationFormId,
                    carrierSolutionId: input.carrierSolutionId,
                    volume: input.volume,
                },
                medicines,
                change: 'add',
                now,
            }),
        ]);
    }
    return true;
}
export async function addProtocolTherapyParameter(tx, input, ctx) {
    assertIsAuthenticated(ctx);
    if (!input.patientProtocolId) {
        throw new Error('patientProtocolId is required');
    }
    if (!Number.isFinite(input.day) || (input.day ?? -1) < 0) {
        throw new Error('Day must be provided when patientProtocolId is provided');
    }
    const patientProtocol = await loadPatientProtocolForTherapy(tx, input.patientProtocolId, input.patientId);
    const { daysRepetition } = patientProtocol.protocol;
    const totalCycles = patientProtocol.cycles ?? patientProtocol.protocol.totalCycles;
    const settings = await getSettings(tx);
    const isZeroDayProtocol = settings?.dayZeroProtocol;
    const context = await loadTherapyParameterContext(tx, input.patientId, input.medicine.map(med => med.activeSubstanceId));
    const startCycleIndex = (input.cycle ?? 1) - 1;
    const cycleIndices = input.recurringCycle
        ? Array.from({ length: Math.max(0, totalCycles - startCycleIndex) }, (_, i) => startCycleIndex + i)
        : [startCycleIndex];
    const created = await Promise.all(cycleIndices.map(cycleIndex => addProtocolTherapyCycle(tx, input, {
        ctx,
        context,
        patientProtocol,
        cycleIndex,
        daysRepetition,
        isZeroDayProtocol,
    })));
    if (!created.some(Boolean)) {
        throw new Error('Prescription date cannot be in the past');
    }
}
//# sourceMappingURL=add-therapy-parameter.js.map